import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2 } from "lucide-react";

const EMPTY = { name: "", labels_any: [], matched_customer: null, job_within_days: "", postcode_prefixes: "", include_international: false };
const err = (e) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : "Request failed"; };
const toBody = (f) => ({ name: f.name, labels_any: f.labels_any, matched_customer: f.matched_customer,
  job_within_days: f.job_within_days ? Number(f.job_within_days) : null,
  postcode_prefixes: String(f.postcode_prefixes || "").split(",").map((s) => s.trim()).filter(Boolean), include_international: !!f.include_international });

export function Counts({ c, testid }) {
  if (!c) return null;
  return (
    <div className="flex flex-wrap gap-3 text-xs" data-testid={testid}>
      <span className="font-semibold text-emerald-700">{c.eligible} eligible{c.eligible_historical ? ` (${c.eligible_historical} historical Superchat)` : ""}</span>
      <span className="text-slate-500">{c.matched} matched</span>
      <span className="text-rose-600">{c.withdrawn} opted out</span>
      <span className="text-rose-600">{c.suppressed} suppressed</span>
      <span className="text-amber-600">{c.unevidenced} no consent date</span>
      <span className="text-slate-400">{c.unknown} no consent</span>
    </div>
  );
}

export function WaSegmentsTab() {
  const [data, setData] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [prev, setPrev] = useState(null);
  const load = () => api.get("/wa/segments").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const preview = async () => { try { setPrev((await api.post("/wa/segments/preview", toBody(f))).data.counts); } catch (e) { toast.error(err(e)); } };
  const save = async () => { try { await api.post("/wa/segments", toBody(f)); toast.success("List saved"); setF(EMPTY); setPrev(null); load(); } catch (e) { toast.error(err(e)); } };
  const del = async (id) => { if (!window.confirm("Delete this list?")) return; try { await api.delete(`/wa/segments/${id}`); load(); } catch (e) { toast.error(err(e)); } };
  const toggle = (l) => setF({ ...f, labels_any: f.labels_any.includes(l) ? f.labels_any.filter((x) => x !== l) : [...f.labels_any, l] });
  if (!data) return null;
  return (
    <div className="space-y-5">
      <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="wa-segment-form">
        <h2 className="text-sm font-bold text-slate-900 mb-1">New customer list</h2>
        <p className="text-xs text-slate-500 mb-3">Only customers with dated marketing consent who are not opted out or suppressed are eligible for campaigns.</p>
        <Input placeholder="List name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="wa-seg-name" />
        <div className="mt-3 text-[11px] uppercase tracking-wide text-slate-400">Labels (any of)</div>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {data.labels.length === 0 && <span className="text-xs text-slate-400">No labels imported yet</span>}
          {data.labels.map((l) => <button key={l} onClick={() => toggle(l)} data-testid={`wa-seg-label-${l}`}
            className={`text-xs rounded-full border px-2.5 py-1 ${f.labels_any.includes(l) ? "bg-indigo-600 text-white border-indigo-600" : "border-slate-200 text-slate-600"}`}>{l}</button>)}
        </div>
        <div className="grid sm:grid-cols-3 gap-3 mt-3">
          <label className="text-xs text-slate-500">Linked to a TedOps customer
            <select value={String(f.matched_customer)} onChange={(e) => setF({ ...f, matched_customer: e.target.value === "null" ? null : e.target.value === "true" })} className="mt-1 block w-full rounded-md border border-slate-200 px-2 py-2 text-sm" data-testid="wa-seg-matched">
              <option value="null">Either</option><option value="true">Yes</option><option value="false">No</option>
            </select></label>
          <label className="text-xs text-slate-500">Job activity in last N days<Input type="number" min="1" value={f.job_within_days} onChange={(e) => setF({ ...f, job_within_days: e.target.value })} data-testid="wa-seg-days" /></label>
          <label className="text-xs text-slate-500">Postcode starts with (comma separated)<Input value={f.postcode_prefixes} placeholder="FY, PR, LA" onChange={(e) => setF({ ...f, postcode_prefixes: e.target.value })} data-testid="wa-seg-postcodes" /></label>
        </div>
        <label className="flex items-center gap-2 mt-3 text-xs text-slate-600"><input type="checkbox" checked={f.include_international} onChange={(e) => setF({ ...f, include_international: e.target.checked })} data-testid="wa-seg-international" />Include international (non +44) numbers — off by default; Meta charges by recipient country</label>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <Button variant="outline" onClick={preview} data-testid="wa-seg-preview">Preview count</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={!f.name.trim()} onClick={save} data-testid="wa-seg-save">Save list</Button>
          <Counts c={prev} testid="wa-seg-preview-counts" />
        </div>
      </section>
      <section className="bg-white border border-slate-200 rounded-2xl shadow-sm divide-y divide-slate-100" data-testid="wa-segment-list">
        {data.items.length === 0 ? <div className="p-5 text-sm text-slate-400">No lists yet.</div> : data.items.map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-3 px-5 py-3" data-testid={`wa-seg-${s.id}`}>
            <div className="min-w-0"><div className="text-sm font-semibold text-slate-800">{s.name}</div><Counts c={s.counts} testid={`wa-seg-counts-${s.id}`} /></div>
            <button onClick={() => del(s.id)} className="text-slate-300 hover:text-rose-500" data-testid={`wa-seg-del-${s.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </section>
    </div>
  );
}
